import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Rocket, Users, Target, TrendingUp, Award, Zap } from 'lucide-react';
import { CodeMonkey } from '../components/CodeMonkey';

const About = () => {
  const aboutSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "About FLUMBERICO",
    "description": "Learn about FLUMBERICO - the world's first tokenization of SEO link juice and equity. Discover our mission, vision, and the team behind the revolutionary SEO social experiment.",
    "url": "https://www.flumberico.fun/about",
    "mainEntity": {
      "@type": "Organization",
      "name": "FLUMBERICO",
      "alternateName": "$FLUMBERICO",
      "url": "https://www.flumberico.fun",
      "logo": "https://www.flumberico.fun/android-chrome-512x512.png",
      "foundingDate": "2024-10-15",
      "founder": {
        "@type": "Organization",
        "name": "FLUMBERICO Team"
      },
      "description": "FLUMBERICO is the world's first tokenization of SEO link juice, creating a revolutionary platform where SEO professionals compete to rank for specific keywords and earn cryptocurrency tokens.",
      "mission": "To democratize SEO equity and create transparent, verifiable ownership of link juice through blockchain tokenization.",
      "slogan": "Ranking is Banking",
      "numberOfEmployees": {
        "@type": "QuantitativeValue",
        "value": "5-10"
      },
      "sameAs": [
        "https://twitter.com/flumberico",
        "https://t.me/flumberico",
        "https://github.com/flumberico"
      ]
    }
  };

  return (
    <div className="min-h-screen bg-black text-white pt-32 pb-20">
      <Helmet>
        <title>About FLUMBERICO - The World's First SEO Token | $FLUMBERICO</title>
        <meta name="description" content="Learn about FLUMBERICO - the revolutionary tokenization of SEO link juice. Discover our mission to democratize SEO equity through blockchain technology." />
        <link rel="canonical" href="https://www.flumberico.fun/about" />
        <script type="application/ld+json">
          {JSON.stringify(aboutSchema)}
        </script>
      </Helmet>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <div className="mb-8 flex justify-center">
            <CodeMonkey size={120} />
          </div>
          <h1 className="text-6xl font-black mb-6 text-gold">
            About FLUMBERICO
          </h1>
          <p className="text-2xl text-silver font-semibold max-w-3xl mx-auto">
            The World's First Tokenization of SEO Link Juice
          </p>
        </div>

        {/* Mission Section */}
        <section className="mb-16 bg-gradient-to-br from-gold/20 via-gold/5 to-black border-2 border-gold/40 rounded-2xl p-8 md:p-12">
          <div className="flex items-center mb-6">
            <Target className="w-12 h-12 text-gold mr-4" />
            <h2 className="text-4xl font-black text-gold">Our Mission</h2>
          </div>
          <p className="text-white text-xl leading-relaxed mb-6">
            To <span className="text-gold font-bold">democratize SEO equity</span> by creating the first-ever platform where link juice and domain authority are tokenized, tradeable, and transparently valued through blockchain technology.
          </p>
          <p className="text-white text-lg leading-relaxed">
            FLUMBERICO transforms the abstract concept of SEO value into a tangible asset. For the first time, the work that SEO professionals do—building backlinks, creating authority, generating organic traffic—has a direct cryptocurrency representation.
          </p>
        </section>

        {/* The Story */}
        <section className="mb-16">
          <div className="flex items-center mb-8">
            <Rocket className="w-12 h-12 text-gold mr-4" />
            <h2 className="text-4xl font-black text-gold">The Story</h2>
          </div>
          
          <div className="bg-gradient-to-br from-silver/10 to-black/50 border border-silver/30 rounded-lg p-8 mb-6">
            <h3 className="text-2xl font-bold text-silver mb-4">October 15, 2025 - Genesis</h3>
            <p className="text-white text-lg leading-relaxed">
              FLUMBERICO was born from a simple yet revolutionary question: <span className="text-gold font-semibold">"What if SEO equity could be owned, traded, and valued like any other asset?"</span>
            </p>
          </div>

          <div className="bg-gradient-to-br from-gold/10 to-black/50 border border-gold/30 rounded-lg p-8">
            <h3 className="text-2xl font-bold text-gold mb-4">The Experiment</h3>
            <p className="text-white text-lg leading-relaxed mb-4">
              We launched an <span className="text-gold font-bold">SEO social experiment</span>: compete to rank #1 for the keyword "flumberico" on Google. Participants build high-authority backlinks to flumberico.fun and earn $FLUMBERICO tokens based on the quality and authority of their links.
            </p>
            <p className="text-white text-lg leading-relaxed">
              This isn't just a ranking competition—it's the <span className="text-gold font-bold">first real-world test of SEO tokenization</span>. As the domain's authority grows, so does its monetization potential through premium link placements. Revenue from these placements burns tokens, creating deflationary pressure that increases value for all holders.
            </p>
          </div>
        </section>

        {/* What Makes Us Unique */}
        <section className="mb-16">
          <div className="flex items-center mb-8">
            <Award className="w-12 h-12 text-gold mr-4" />
            <h2 className="text-4xl font-black text-gold">What Makes Us Unique</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-black/60 border border-gold/30 rounded-lg p-6">
              <Zap className="w-10 h-10 text-gold mb-4" />
              <h3 className="text-xl font-bold text-gold mb-3">First of Its Kind</h3>
              <p className="text-white">
                FLUMBERICO is the world's first cryptocurrency token that represents SEO equity and link juice. No one has ever tokenized domain authority before.
              </p>
            </div>

            <div className="bg-black/60 border border-silver/30 rounded-lg p-6">
              <TrendingUp className="w-10 h-10 text-silver mb-4" />
              <h3 className="text-xl font-bold text-silver mb-3">Transparent Value</h3>
              <p className="text-white">
                Domain authority and backlink metrics are verifiable on Ahrefs, Moz, and SEMrush. Token value is directly tied to measurable, public SEO data.
              </p>
            </div>

            <div className="bg-black/60 border border-gold/30 rounded-lg p-6">
              <Users className="w-10 h-10 text-gold mb-4" />
              <h3 className="text-xl font-bold text-gold mb-3">Community-Driven</h3>
              <p className="text-white">
                SEO professionals worldwide contribute to building FLUMBERICO's domain authority. The more they contribute, the more they earn.
              </p>
            </div>

            <div className="bg-black/60 border border-silver/30 rounded-lg p-6">
              <Target className="w-10 h-10 text-silver mb-4" />
              <h3 className="text-xl font-bold text-silver mb-3">Real Revenue Model</h3>
              <p className="text-white">
                50% of all advertising revenue from premium link placements goes directly to burning $FLUMBERICO tokens, creating sustainable value appreciation.
              </p>
            </div>
          </div>
        </section>

        {/* The Team */}
        <section className="mb-16 bg-gradient-to-br from-gold/10 to-black/50 border border-gold/30 rounded-lg p-8">
          <div className="flex items-center mb-6">
            <Users className="w-12 h-12 text-gold mr-4" />
            <h2 className="text-4xl font-black text-gold">The Team</h2>
          </div>
          <p className="text-white text-xl leading-relaxed mb-6">
            FLUMBERICO is built by a team of <span className="text-gold font-bold">SEO professionals, blockchain developers, and digital marketing experts</span> who saw the opportunity to revolutionize how we think about SEO value.
          </p>
          <p className="text-white text-lg leading-relaxed mb-6">
            We've spent years in the trenches of link building, watching domains appreciate in value through accumulated authority. We asked: why shouldn't the people building that authority directly benefit from its appreciation?
          </p>
          <div className="bg-black/50 rounded-lg p-6 border-l-4 border-gold">
            <p className="text-silver text-lg italic">
              "For decades, domain authority has been valuable but intangible. FLUMBERICO makes it tangible, tradeable, and transparent. This is the future of SEO value."
            </p>
            <p className="text-gold font-bold mt-4">— FLUMBERICO Team</p>
          </div>
        </section>

        {/* Why This Matters */}
        <section className="mb-16">
          <h2 className="text-4xl font-black text-gold mb-6">Why This Matters</h2>
          <div className="bg-gradient-to-br from-silver/20 to-black/50 border border-silver/40 rounded-lg p-8">
            <p className="text-white text-lg leading-relaxed mb-4">
              SEO professionals have been creating immense value for decades—building backlinks, generating domain authority, driving organic traffic. But that value has always been locked within individual websites, impossible to extract, trade, or fairly compensate.
            </p>
            <p className="text-white text-lg leading-relaxed mb-4">
              <span className="text-gold font-bold">FLUMBERICO changes everything.</span> Now, the work SEO professionals do can be rewarded immediately with cryptocurrency tokens. Link equity becomes liquid. Domain authority becomes tradeable.
            </p>
            <p className="text-white text-lg leading-relaxed">
              This isn't just a meme coin—it's the <span className="text-gold font-bold">foundation for an entirely new asset class</span>. As more domains tokenize their SEO equity, we're building the infrastructure for a decentralized SEO economy.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="text-center bg-gradient-to-r from-gold/20 via-gold/10 to-silver/20 border-2 border-gold/50 rounded-2xl p-12">
          <h2 className="text-4xl font-black text-gold mb-6">Join the Revolution</h2>
          <p className="text-white text-xl mb-8 max-w-2xl mx-auto">
            Be part of the world's first SEO tokenization experiment. Start building links, earning tokens, and shaping the future of SEO value.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/how-to-earn">
              <button className="font-bold text-lg px-8 py-4 rounded-full transition-transform hover:scale-105" style={{ backgroundColor: '#FFD700', color: '#000000' }}>
                Start Earning $FLUMBERICO
              </button>
            </Link>
            <a href="https://t.me/flumberico" target="_blank" rel="noopener noreferrer">
              <button 
                className="font-bold text-lg px-8 py-4 rounded-full transition-all"
                style={{ border: '2px solid #C0C0C0', color: '#C0C0C0', backgroundColor: 'transparent' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#FFFFFF';
                  e.currentTarget.style.color = '#000000';
                  e.currentTarget.style.borderColor = '#FFFFFF';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#C0C0C0';
                  e.currentTarget.style.borderColor = '#C0C0C0';
                }}
                aria-label="Join FLUMBERICO official Telegram channel"
              >
                Join Our Telegram
              </button>
            </a>
          </div>
        </section>
      </div>
    </div>
  );
};

export default About;
